import type { AppointmentSlot } from '@/types/scheduling-api';
import type { SlotsDataModel, SlotDetail } from '@/services/scheduling/use-retrieve-slots';

/**
 * Pure conflict-resolution logic for the recurrence picker (PROD-932).
 *
 * Mirrors Flutter `recurrence_appointment_widget.dart` (the inline
 * skip / edit / pick-suggested actions) + `slots_data_model.dart`
 * (`getSlotStatus()` / `doctorChanged()`) + `scheduling_bloc.dart:1264-1277`
 * (the `saveEnabled` recompute). Kept framework-free so it can be unit
 * tested without RHF / React Query context — same split as
 * `block-availability-validation.ts`.
 */

/** Verbatim from `scrips_core/lib/constants/app_constants.dart:802-804`.
 * Note the space + lowercase 'a' in "Not available" — the backend ships
 * exactly this string and a prior comment in use-retrieve-slots.ts had it
 * wrong ("NotAvailable"), which silently broke the conflict count. */
export const SLOT_STATUS_NOT_AVAILABLE = 'Not available';
export const SLOT_STATUS_AVAILABLE = 'Available';
export const SLOT_STATUS_SKIPPED = 'Skipped';

export type SlotStatus =
  | typeof SLOT_STATUS_NOT_AVAILABLE
  | typeof SLOT_STATUS_AVAILABLE
  | typeof SLOT_STATUS_SKIPPED;

/**
 * Flutter `SlotDetail.getSlotStatus()` (slots_data_model.dart):
 *   slot != null        -> "Available"   (already on the doctor's calendar)
 *   blank slotStatus    -> "Not available"
 *   else                -> slotStatus    ("Available" once a slot is picked,
 *                                          or "Skipped")
 */
export function effectiveSlotStatus(detail: SlotDetail): SlotStatus {
  if (detail.slot != null) return SLOT_STATUS_AVAILABLE;
  const raw = detail.slotStatus;
  if (raw == null || raw.trim() === '') return SLOT_STATUS_NOT_AVAILABLE;
  return raw as SlotStatus;
}

/**
 * Drives the "Doctor has been changed." note. Two sources:
 *  - Backend-derived (Flutter `SlotDetail.doctorChanged()`): a slot came back
 *    under a DIFFERENT provider with no nearest slots offered.
 *  - User-driven (PROD-1011): the user reassigned this occurrence to another
 *    doctor via "Select another doctor" — recorded as an explicit flag since
 *    `nearestSlots` may be non-empty in that case.
 */
export function isDoctorChanged(detail: SlotDetail): boolean {
  // A user re-selection records an EXPLICIT boolean (true OR false) — that's
  // authoritative and must NOT fall through to the derived check. Otherwise a
  // same-doctor re-pick on a no-suggestion occurrence (`nearestSlots: []` +
  // `selectedSlot` set + explicit `false`) would mis-derive as changed and
  // re-show "Select another doctor". `applyEdit`/`applySkip`/`applyUndo` reset
  // the flag, so a reverted occurrence re-derives cleanly (PROD-1011 review §4).
  if (detail.isDoctorChanged !== undefined) return detail.isDoctorChanged;
  // Backend-derived (Flutter `SlotDetail.doctorChanged()`): a slot came back
  // under a DIFFERENT provider with no nearest slots offered.
  return (
    detail.nearestSlots != null &&
    detail.nearestSlots.length === 0 &&
    detail.selectedSlot != null
  );
}

/**
 * Flutter `scheduling_bloc.dart:1264-1277` — Save is enabled once NO
 * occurrence is left in "Not available". A confirmed (`slot`), replaced
 * (`selectedSlot` + status "Available"), or "Skipped" occurrence all count
 * as resolved.
 *
 * Beyond the Flutter bloc: we ALSO require at least one occurrence that
 * actually books. The bloc's rule (`count(NotAvailable) == 0`) returns true
 * for an all-skipped set, which would POST an empty `slots` array — a no-op
 * the user can't tell apart from a real booking. Block it here (PR #383
 * review — Samer #3). See `countActiveOccurrences`.
 */
export function computeSaveEnabled(slotsData: SlotsDataModel | null): boolean {
  if (!slotsData) return false;
  const details = slotsData.slotDetails ?? [];
  const noneUnresolved = details.every(
    (d) => effectiveSlotStatus(d) !== SLOT_STATUS_NOT_AVAILABLE,
  );
  return noneUnresolved && countActiveOccurrences(slotsData) >= 1;
}

/** Count of occurrences in "Not available" — drives the conflict summary count. */
export function countUnresolved(slotsData: SlotsDataModel | null): number {
  const details = slotsData?.slotDetails ?? [];
  return details.filter((d) => effectiveSlotStatus(d) === SLOT_STATUS_NOT_AVAILABLE).length;
}

/**
 * Active (non-skipped) occurrence count — Flutter recomputes
 * `recurrenceProperties.numberOfRepeat` to this on every skip / undo
 * (widget 789-795, 913-920, 1080-1085).
 */
export function countActiveOccurrences(slotsData: SlotsDataModel | null): number {
  const details = slotsData?.slotDetails ?? [];
  return details.filter((d) => effectiveSlotStatus(d) !== SLOT_STATUS_SKIPPED).length;
}

/** Immutably replace one occurrence with a patched copy. */
function patchDetail(
  slotsData: SlotsDataModel,
  index: number,
  patch: Partial<SlotDetail>,
): SlotsDataModel {
  const details = (slotsData.slotDetails ?? []).map((d, i) =>
    i === index ? { ...d, ...patch } : d,
  );
  return { ...slotsData, slotDetails: details };
}

/**
 * pick-suggested — tap a backend-suggested slot for a "Not available"
 * occurrence (widget 1022-1029). Sets status "Available" + the chosen slot.
 */
export function applyPickSuggested(
  slotsData: SlotsDataModel,
  index: number,
  slot: AppointmentSlot,
): SlotsDataModel {
  return patchDetail(slotsData, index, {
    slotStatus: SLOT_STATUS_AVAILABLE,
    selectedSlot: slot,
    isSkipped: false,
  });
}

/**
 * skip — "Skip this occurrence" (widget 910-912 / 1078-1085). Drops the
 * occurrence from the booking; clears any picked slot + re-selected doctor.
 */
export function applySkip(slotsData: SlotsDataModel, index: number): SlotsDataModel {
  return patchDetail(slotsData, index, {
    slotStatus: SLOT_STATUS_SKIPPED,
    isSkipped: true,
    selectedSlot: undefined,
    selectedDoctor: undefined,
    // `undefined` (not `false`) so the occurrence re-derives cleanly if revived.
    isDoctorChanged: undefined,
  });
}

/** undo — un-skip a "Skipped" occurrence back to "Not available" (widget 787-788). */
export function applyUndo(slotsData: SlotsDataModel, index: number): SlotsDataModel {
  return patchDetail(slotsData, index, {
    slotStatus: SLOT_STATUS_NOT_AVAILABLE,
    isSkipped: false,
    selectedSlot: undefined,
    selectedDoctor: undefined,
    isDoctorChanged: undefined,
  });
}

/**
 * edit — revert a picked/replaced occurrence to "Not available" so the user
 * can pick again (widget 886-895). Also clears any re-selected doctor so the
 * occurrence falls back to its original provider on a fresh pick.
 */
export function applyEdit(slotsData: SlotsDataModel, index: number): SlotsDataModel {
  return patchDetail(slotsData, index, {
    slotStatus: SLOT_STATUS_NOT_AVAILABLE,
    selectedSlot: undefined,
    selectedDoctor: undefined,
    isDoctorChanged: undefined,
  });
}

/**
 * reselect — PROD-1011. Write back a slot picked via "See all" (same doctor)
 * or "Select another doctor" (different doctor). The picked `slot` carries the
 * resolved `providerId` (that's what books); `doctor` + `doctorChanged` drive
 * the "Doctor has been changed." note. Mirrors Flutter's write into
 * `selectedSlotDetail` after `select_doctor_widget` / the recurrence date-time
 * picker returns (recurrence_appointment_widget.dart write-back).
 */
export function applyReselect(
  slotsData: SlotsDataModel,
  index: number,
  opts: { slot: AppointmentSlot; doctor?: { id: string; name: string }; doctorChanged: boolean },
): SlotsDataModel {
  return patchDetail(slotsData, index, {
    slotStatus: SLOT_STATUS_AVAILABLE,
    selectedSlot: opts.slot,
    selectedDoctor: opts.doctor,
    isDoctorChanged: opts.doctorChanged,
    isSkipped: false,
  });
}
